import '../../overlays/as-hovercard-wrapper.ts';
import { hovercardStyles } from '../../overlays/as-hovercard.ts';
import '../../text-and-lang/as-text.ts';
import { sendEv } from '../../../data/events.ts';

/**
 * Shared `confirm-on-click` panel for the button components.
 *
 * A button with `confirm-on-click` doesn't fire on click. The click opens a
 * small hovercard asking the user to confirm, and only the panel's action
 * button dispatches `as-button-click`. as-button, as-icon-button and
 * as-text-button all use this — the markup and the wiring were copied into each
 * one, with the two copies having already drifted apart on the default text and
 * the action-text attribute name.
 *
 * Never window.confirm for this: a native modal takes OS focus off the page and
 * an Electron host hands it back to the window without putting it back into the
 * web contents, leaving every text input dead. See as-file-input._confirmReplace.
 *
 * Attributes (add CONFIRM_ON_CLICK_ATTRS to observedAttributes):
 * - `confirm-on-click`             turn it on
 * - `confirm-default-text`         the question (default "Are you sure?")
 * - `confirm-dict-key`             dict key for the question
 * - `confirm-action-default-text`  the action button (default "Continue")
 * - `confirm-action-dict-key`      dict key for the action button
 * - `confirm-placement`            where the panel opens (default "bottom")
 *
 * Dismissing is a plain click outside the panel, which the hovercard wrapper
 * handles in toggle-on-click mode.
 */
export const CONFIRM_ON_CLICK_ATTRS = [
  'confirm-on-click', 'confirm-default-text', 'confirm-dict-key',
  'confirm-action-default-text', 'confirm-action-text', 'confirm-action-dict-key',
  'confirm-placement',
] as const;

/** Card layout. The card's own box styling comes from hovercardStyles, which a
 *  shadow root has to adopt for itself — the copy as-hovercard puts on the
 *  document can't cross the boundary. Both are in confirmOnClickSheets. */
const cardStyles = (() => {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(`
    .confirm-card {
      padding: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      align-items: center;
      min-width: 150px;
    }

    .confirm-text {
      font-size: 0.875rem;
      text-align: center;
      color: #333;
    }

    .confirm-action-btn {
      width: 100%;
    }
  `);
  return sheet;
})();

/** Adopt these in the shadow root of any button using wrapWithConfirm(). */
export const confirmOnClickSheets: CSSStyleSheet[] = [hovercardStyles, cardStyles];

const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Wrap rendered button markup in the confirmation hovercard, or hand it back
 * untouched when the host has no `confirm-on-click`. The host must also import
 * as-button, which the panel's action button uses.
 */
export function wrapWithConfirm(host: HTMLElement, buttonHTML: string): string {
  if (!host.hasAttribute('confirm-on-click')) return buttonHTML;

  const text = host.getAttribute('confirm-default-text') || 'Are you sure?';
  const dictKey = host.getAttribute('confirm-dict-key');
  // confirm-action-text is as-button's original name for this, kept working.
  const actionText = host.getAttribute('confirm-action-default-text')
    || host.getAttribute('confirm-action-text') || 'Continue';
  const actionDictKey = host.getAttribute('confirm-action-dict-key');
  const placement = host.getAttribute('confirm-placement') || 'bottom';

  return /*html*/`
    <as-hovercard-wrapper toggle-on-click show-on-hover="false" placement="${esc(placement)}" offset="12">
      <div slot="trigger" style="display:contents">${buttonHTML}</div>
      <as-hovercard slot="hovercard">
        <div class="confirm-card">
          <as-text class="confirm-text" ${dictKey ? `dict-key="${esc(dictKey)}"` : ''} default-text="${esc(text)}"></as-text>
          <as-button class="confirm-action-btn" size="s" ${actionDictKey ? `dict-key="${esc(actionDictKey)}"` : ''} default-text="${esc(actionText)}"></as-button>
        </div>
      </as-hovercard>
    </as-hovercard-wrapper>
  `;
}

// Marks an action button whose listener is already attached. The buttons
// re-run their listener setup on both render and connect, and the panel isn't
// rebuilt in between, so without this the callback would fire twice.
const wired = new WeakSet<Element>();

/** Wire the panel's action button: run `onConfirmed`, then close the panel. */
export function attachConfirmListener(host: HTMLElement, onConfirmed: () => void): void {
  const btn = host.shadowRoot?.querySelector('.confirm-action-btn');
  if (!btn || wired.has(btn)) return;
  wired.add(btn);
  btn.addEventListener('as-button-click', (ev: Event) => {
    ev.stopPropagation();
    onConfirmed();
    sendEv(host.shadowRoot?.querySelector('as-hovercard'), 'as-hovercard-close');
  });
}
